import { useEffect, useState } from 'react'
import React, { useGlobal } from 'reactn'
import { useNavigate, useParams } from 'react-router-dom'
import { jstNewDate } from 'ui/utils/jstDatetime'
import ReservationComponent from 'ui/pages/applicant/Reservation'
import ReservationDone from 'ui/pages/applicant/ReservationDone'
import SystemError from 'ui/pages/med/SystemError'
import Loading from 'ui/pages/applicant/Loading'
import {
  PreReservationExpired,
  ReceptionEmpty,
  ReceptionExpired,
  ReservationReserved,
} from 'ui/pages/applicant/ReceptionMessage'
import PasscodeEntry from 'ui/pages/applicant/PasscodeEntry'

import {
  getAllImages,
  getBank,
  getBilling,
  getDepartmentSettings,
  getMed,
  getNearPharmacies,
  getPrescription,
  getReservation,
  getReservationFrames,
  getReservationIds,
  getStripeKeys,
  getVcUrl,
  postReservation,
  putReservation,
  putReservationCancel,
  searchPharmacies,
  getGoogleMapsApiKey,
  postGoogleMapsApiError,
  getReservableDates,
  getEligibilityVerificationAppId,
} from './fetch'

import type { AttachmentImageNameProps } from './fetch'
import type {
  BankTransfer,
  Billing,
  DepartmentSettingsProps,
  PrescriptionSetting,
  ReservationFrameProps,
} from 'types/med'
import {
  ReservedReservationProps,
  MedicalInstitutionProps,
  ReservationFormProps,
  PasscodeEntryApiProps,
  EligibilityVerificationUrlSettingProps,
} from 'types/applicant'
import type { CallMethodNames, PharmacyProps } from 'types/common'
import ReservationRead from 'ui/pages/applicant/ReservationRead'
import ReservationCancelled from 'ui/pages/applicant/ReservationCancelled'
import nullToUndefined from 'ui/utils/nullToUndefined'
import { parseDatetimeSecondString } from 'ui/utils/dateFormatUtils'
import { isPastJstNow } from 'ui/utils/jstDatetime'
import GaEvents from '../shared/GaEvents'
import {
  getMonth,
  setMonth,
  setDate,
  lastDayOfMonth,
  format,
  getYear,
  setYear,
} from 'date-fns'
import { DateString } from 'config/format'
import {
  ApiErrorWithMessage,
  ApiErrorWithNoMessage,
  NotFoundError,
  TimeoutError,
} from 'app/client'
import { ImageProps } from 'types/common'
import { errorToString } from '../shared/errorToString'

const Reservation: React.FC = () => {
  const navigate = useNavigate()

  const { institution_access_key, access_key } = useParams<
    'institution_access_key' | 'access_key'
  >() as {
    institution_access_key: string
    access_key: string
  }

  // NOTE: 現在のパスを元にした相対パスなのでURLが変わった時に注意する
  const receptionUrl = `../../../reception/${institution_access_key}`

  const [googleMapsApiKey, setGoogleMapsApiKey] = useState<string>()

  /**
   * NOTE: 医療機関データ
   */
  const [med, setMed] = useState<MedicalInstitutionProps | undefined>()
  /**
   * NOTE: 予約済みで予約IDが発行されている場合はこのデータを取得しパスコード入力画面を表示
   */
  const [reservationIds, setReservationIds] = useState<
    PasscodeEntryApiProps | undefined
  >()
  /**
   * NOTE: 予約者データ
   */
  const [reservation, setReservation] = useState<
    ReservedReservationProps | undefined
  >()

  /**
   * NOTE: 診療科id
   */
  const [departmentId, setDepartmentId] = useState<number | undefined>(
    undefined
  )
  /**
   * NOTE: 診療科予約設定データ
   */
  const [departmentSettings, setDepartmentSettings] = useState<
    DepartmentSettingsProps | undefined
  >()

  /**
   * NOTE: 日付に対応する予約枠データ
   */
  const [reservationFrames, setReservationFrames] = useState<
    ReservationFrameProps[] | undefined
  >()

  /**
   * NOTE: 選択された月に対する予約可能日リスト デフォルト値は必ず今月から画面が開くことが前提

   */
  const [currentMonth, setCurrentMonth] = useState<{
    year: number
    month: number
  }>({
    year: getMonth(jstNewDate),
    month: getYear(jstNewDate),
  })

  /**
   * NOTE: 選択された月に対する予約可能日リスト
   */
  const [reservableDates, setReservableDates] = useState<
    { reservable_date: string }[]
  >([])

  /**
   * NOTE: データ送信が完了したかかどうか
   */
  const [finished, setFinished] = useState<boolean>(false)

  /**
   * NOTE: 有効なURLかどうか
   */
  const [empty, setEmpty] = useState<boolean>(false)

  // NOTE: 有効期限切れかどうか
  const [expired, setExpired] = useState(false)

  // NOTE: すでに予約完了しているかどうか(二つ画面を開いて二重送信した場合など）
  const [alreadyFinished, setAlreadyFinished] = useState(false)

  // NOTE: 仮予約からのフォームが有効期限切れかどうか
  const [preReservationExpired, setPreReservationExpired] = useState(false)

  /**
   * NOTE: 閲覧モード
   */
  const [read, setRead] = useState<boolean>(false)

  /**
   * NOTE: 閲覧モード
   */
  const [passCode, setPassCode] = useState<string | undefined>()

  /**
   * NOTE: キャンセル状態
   */
  const [cancelled, setCancelled] = useState<boolean>(false)

  const [prescriptionSetting, setPrescriptionSetting] =
    useState<PrescriptionSetting>()

  const [nearPharmacies, setNearPharmacies] = useState<PharmacyProps[]>([])

  /**
   * NOTE: 決済情報
   */
  const [billingSettings, setBillingSettings] = useState<{
    billings?: Billing[]
    bankTransfer?: BankTransfer
  }>({})

  /**
   * NOTE: オンライン資格確認設定値
   */
  const [
    eligibilityVerificationUrlSetting,
    setEligibilityVerificationUrlSetting,
  ] = useState<EligibilityVerificationUrlSettingProps>()

  /**
   * システムエラーステータス
   */
  const [systemError, setSystemError] = useState<boolean>(false)

  const [, setError] = useGlobal('error')

  /**
   * 予約データ取得
   *
   * @param med
   * @param pass_code
   */
  const getReservationHandler = (pass_code?: string) =>
    getReservation({
      access_key,
      pass_code, // NOTE: 予約IDがある時は必須
    })
      .catch((e) => {
        if (e instanceof NotFoundError) {
          setEmpty(true)
          return
        }

        if (e instanceof ApiErrorWithNoMessage) {
          switch (e.response.status) {
            case 401:
              setEmpty(true)
              break
            default:
              setSystemError(true)
          }
          return
        }
        if (e instanceof ApiErrorWithMessage) {
          if (
            e.has({
              type: 'validation_error',
              key: 'institution_access_key',
            }) ||
            e.has({
              key: 'reservation_id',
              value: 'cancel',
            })
          ) {
            setEmpty(true)
            return
          }
          if (
            e.has({
              key: [
                'provisional_reservation_access_key_expiration',
                'pass_code',
              ],
              value: 'valid',
            })
          ) {
            setPreReservationExpired(true)
          }
          if (
            e.has({
              key: ['provisional_reservation_access_key', 'access_key'],
              value: 'exists',
            })
          ) {
            setEmpty(true)
            return
          }
          // NOTE: 以下の場合のみPasscodeEntry側でキャッチさせる
          if (
            pass_code &&
            e.has({
              key: 'pass_code',
              value: 'correct',
            })
          ) {
            throw e
          }
        }
        throw e
      })
      .then((data) => {
        pass_code && setPassCode(pass_code)
        setReservation(data as ReservedReservationProps)
        /**
         * NOTE: 添付画像があれば全て取得してセット
         */
        const additional: AttachmentImageNameProps[] = [
          'attachment_images', // NOTE: 予約IDが存在する時だけ取得
        ]
        const names: AttachmentImageNameProps[] = [
          'insurance_card_images',
          'certificate_images',
          'identity_verification_images',
          ...(pass_code ? additional : []),
        ]
        return getAllImages({
          pass_code,
          names,
          access_key,
        }).then((results) => {
          results.forEach((images: ImageProps[], index) => {
            if (images.length) {
              const name = names[index]
              setReservation(
                (prevValues: ReservedReservationProps | undefined) => {
                  if (prevValues && !prevValues[name]?.length) {
                    return { ...prevValues, [name]: images }
                  }
                }
              )
            }
          })
        })
      })
      .catch((e) => {
        if (pass_code) throw e
        if (e instanceof NotFoundError) {
          setEmpty(true)
          return
        }
        if (e instanceof ApiErrorWithNoMessage) {
          return
        }
        if (e instanceof ApiErrorWithMessage) {
          if (
            e.has({
              type: 'validation_error',
              key: 'access_key',
            })
          ) {
            setEmpty(true)
          }
          return
        }
        // NOTE: その他の予期しないエラー
        setSystemError(true)
      })

  const getBillingHandler = () =>
    getBilling(access_key)
      .then((billings) => setBillingSettings((prev) => ({ ...prev, billings })))
      .catch((e) => {
        if (e.response?.status === 404) {
          return
        }
        // NOTE: その他エラーも無視
        return
      })

  const getBankHandler = () =>
    getBank(access_key)
      .then((bankTransfer) =>
        setBillingSettings((prev) => ({ ...prev, bankTransfer }))
      )
      .catch((e) => {
        if (e.response?.status === 404) {
          return
        }
        // NOTE: その他エラーも無視
        return
      })

  useEffect(() => {
    /**
     * NOTE: 医療機関データセット
     */
    !med &&
      getMed(institution_access_key)
        .then(setMed)
        .catch((e) => {
          if (e instanceof ApiErrorWithMessage) {
            // NOTE: 医療機関アクセスキーが存在しない400エラーのみ
            setError('invalidUrl')
          } else {
            setSystemError(true)
          }
        })
    med &&
      !reservation &&
      /**
       * NOTE: 予約IDデータがあればセット
       */
      getReservationIds(access_key)
        .then(setReservationIds)
        .catch((e) => {
          if (e instanceof NotFoundError) {
            //  NOTE: このaccess_keyでデータが存在しなければ無効なURL
            setEmpty(true)
          } else {
            setSystemError(true)
          }
        })
    // NOTE: 現在はrouterでこのコンポーネントにくる際はaccess_key,institution_access_key必須
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [med, reservation])

  useEffect(() => {
    if (!reservationIds) return

    // NOTE: 予約が作成されておらず有効期限が切れていたら再度メール認証を促す画面
    if (
      !reservationIds.reservation_number &&
      isPastJstNow(parseDatetimeSecondString(reservationIds.expiration))
    ) {
      setExpired(true)
      return
    }

    // NOTE: reservation_numberが存在しなければ予約者データセット
    if (!reservationIds.reservation_number) {
      med && getReservationHandler()
    } else {
      setRead(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservationIds])

  /**
   * NOTE: 初期は最初の診療科を選択状態に
   * reservation_department_idがある時はdepartmentIdにセット
   */
  useEffect(() => {
    if (!med || !reservation) {
      return
    }
    if (reservation.reservation_department_id) {
      // NOTE: 予約済み
      setDepartmentId(reservation.reservation_department_id)
    } else if (med.departments.length) {
      // NOTE: 予約前
      !departmentId && setDepartmentId(med.departments[0].id)
    } else {
      // NOTE: 予約前で公開科目なし
      setEmpty(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [med, reservation])

  useEffect(() => {
    if (access_key) {
      getGoogleMapsApiKey(access_key)
        .then(({ api_key }) => setGoogleMapsApiKey(api_key))
        .catch(console.log) // NOTE: access_keyが存在しない時403をスキップ
      !billingSettings?.billings && getBillingHandler()
      !billingSettings?.bankTransfer && getBankHandler()
      getPrescription(access_key)
        .then(setPrescriptionSetting)
        .catch((e) => {
          if (
            e instanceof TimeoutError ||
            ((e instanceof ApiErrorWithNoMessage ||
              e instanceof ApiErrorWithMessage) &&
              e.response.status >= 500)
          ) {
            setPrescriptionSetting({
              prescription_medicine: null,
              is_todokusuri: null,
              server_error: true,
            })
            return
          }
          // NOTE: access_keyが存在しない時403をスキップ
          console.log(e)
        })
      getNearPharmacies(access_key).then(setNearPharmacies).catch(console.log) // NOTE: access_keyが存在しない時403をスキップ
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [access_key])

  /**
   * NOTE: 診療科を選択したときにIDをセット
   * @param e
   */
  const selectDepartmentHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = (e.target as HTMLInputElement).value
    setDepartmentId(parseInt(value))
  }

  useEffect(() => {
    /**
     * NOTE: 診療科の予約設定取得
     */
    departmentId &&
      getDepartmentSettings({
        departmentId,
        access_key,
      })
        .then(setDepartmentSettings)
        .catch(() => {
          setEmpty(true)
        })
    // NOTE: 現在はrouterでこのコンポーネントにくる際はaccess_key必須
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentId])

  useEffect(() => {
    const firstDate = setDate(
      setMonth(setYear(jstNewDate, currentMonth.year), currentMonth.month),
      1
    )
    const from_date = format(firstDate, DateString)
    const to_date = format(lastDayOfMonth(firstDate), DateString)
    departmentId &&
      getReservableDates(departmentId, { access_key, from_date, to_date }).then(
        setReservableDates
      )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth, departmentId])

  useEffect(() => {
    if (med?.online_eligibility_verification_url) {
      getEligibilityVerificationAppId(access_key).then((data) =>
        setEligibilityVerificationUrlSetting({
          url: med.online_eligibility_verification_url,
          appId: data.online_eligibility_verification_app_id,
        })
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [med?.online_eligibility_verification_url])

  const reservationCancelHandler = () =>
    putReservationCancel({
      access_key,
      pass_code: passCode,
    }).then(() => setCancelled(true))

  /**
   * NOTE: 予約枠データ取得
   * @param date
   */
  const getReservationFrameHandler = (date: Date) => {
    departmentId &&
      getReservationFrames({
        departmentId,
        access_key,
        date,
      })
        .then(setReservationFrames)
        .catch((e) => {
          if (e instanceof ApiErrorWithMessage) {
            setEmpty(true)
          } else {
            setSystemError(true)
          }
        })
  }

  /**
   * NOTE: 予約データ送信
   * @param data
   */
  const submitHandler = (data: ReservationFormProps) => {
    // NOTE: 送信後レスポンスからエラーを振り分け
    const apiError = (e: Error) => {
      if (e instanceof ApiErrorWithMessage) {
        if (
          // NOTE: アクセスキーが存在しない
          e.has({
            key: 'access_key',
            value: 'exists',
          }) ||
          // NOTE: キャンセルされた予約
          e.has({
            key: 'reservation_id',
            value: 'cancel',
          }) ||
          // NOTE: 存在しない診療科目
          e.has({
            key: 'department_id',
            value: 'exists',
          }) ||
          // NOTE: 予約不可の日時が指定された, 予約枠が埋まっている
          e.has({
            key: 'reservation_datetime_start',
            value: ['valid', 'capacity'],
          }) || // NOTE: 予約枠時間が不正
          e.has({
            key: 'reservation_time_period',
            value: 'valid',
          }) ||
          // NOTE: 通話方法、支払い方法が存在しない
          e.has({
            key: ['call_method', 'settlement_method'],
            value: 'exists',
          }) ||
          // NOTE: 添付画像の形式が不正
          e.has({
            key: [
              'attachment_images',
              'identity_verification_images',
              'insurance_card_images',
              'certificate_images',
            ],
            value: 'mime_type',
          }) || // NOTE: 仮予約から予約しようとしているが、メールアドレス認証時に指定された仮予約アクセスキーが存在しない
          e.has({
            key: 'provisional_reservation_access_key',
            value: 'exists',
          })
        ) {
          setEmpty(true)
          return
        }
        if (
          // NOTE: パスコードが不正
          e.has({
            key: 'pass_code',
            value: 'valid',
          }) ||
          // NOTE: 新規予約のアクセスキーの有効期限が切れている
          e.has({
            key: 'access_key_expiration',
            value: 'valid',
          }) ||
          // NOTE: 予約枠が予約可能日時を過ぎている
          e.has({
            key: 'reservation_datetime_start',
            value: 'deadline',
          }) ||
          // NOTE: 仮予約から予約しようとしているが、仮予約アクセスキーが有効期限切れ
          e.has({
            key: 'provisional_reservation_access_key_expiration',
            value: 'valid',
          })
        ) {
          setExpired(true)
          return
        }

        // NOTE: すでに予約完了しているかどうか(二つ画面を開いて二重送信した場合など）
        if (
          e.has({
            type: 'validation_error',
            key: 'access_key',
            value: 'unique',
          })
        ) {
          setAlreadyFinished(true)
          return
        }

        // NOTE: scuel_id,how_to_receive_medicineが必須の時になんらかの理由で存在しない場合
        if (
          e.has({
            type: 'validation_error',
            key: ['scuel_id', 'how_to_receive_medicine'],
            value: 'required',
          })
        ) {
          setSystemError(true)
          return
        }

        throw e
      }
      throw e
    }

    if (reservationIds?.reservation_number) {
      return putReservation({
        data: { pass_code: passCode, ...data },
        access_key,
      })
        .then(() => {
          setFinished(true)
        })
        .catch(apiError)
    } else {
      return postReservation(data)
        .then(() => {
          setFinished(true)
          med && GaEvents.personalReservationFinished({ med })
        })
        .catch(apiError)
    }
  }

  const videoChatStartHandler = (app: CallMethodNames) =>
    getVcUrl(app)(access_key)
      .then((data) => {
        const name = window.open(data.url, 'zoomUrl')
        if (name === null) {
          window.location.assign(data.url)
        } else {
          throw new Error('open window')
        }
      })
      .catch((e) => {
        if (
          app === 'video_zoom' &&
          e instanceof ApiErrorWithMessage &&
          e.has({
            type: 'server_error',
            key: 'token',
            value: 'valid',
          })
        ) {
          navigate(
            `/personal/meeting/${app}/${institution_access_key}/${access_key}`
          )
        } else {
          // その他の予期しないエラーはReservationReadのcatchに渡す
          throw e
        }
      })

  if (systemError) {
    return <SystemError type={'personal'} />
  }

  if (med && expired) {
    const expiredReceptionUrl = reservationIds?.pharmacy_service_key
      ? `${receptionUrl}?pharmacy=${reservationIds.pharmacy_service_key}`
      : receptionUrl
    return <ReceptionExpired med={med} receptionUrl={expiredReceptionUrl} />
  }

  if (med && preReservationExpired) {
    return <PreReservationExpired med={med} />
  }

  if (med && empty) {
    return <ReceptionEmpty med={med} />
  }

  if (med && reservationIds && cancelled) {
    return <ReservationCancelled med={med} entry={reservationIds} />
  }

  // NOTE: すでに予約完了している場合(二つ画面を開いて二重送信した場合など）
  if (med && reservation && alreadyFinished) {
    return (
      <ReservationReserved med={med} type={reservation.verification_type} />
    )
  }

  /**
   * NOTE: 予約IDあり、予約データ未取得の時パスコード入力画面
   */
  if (med && reservationIds?.reservation_number && !reservation) {
    return (
      <PasscodeEntry
        med={med}
        entry={reservationIds}
        onSubmit={(data) =>
          data.pass_code && getReservationHandler(data.pass_code)
        }
      />
    )
  }

  /**
   * NOTE: 医療機関、診療科ID、予約データ取得
   */
  if (reservation && departmentSettings && med && departmentId) {
    /**
     * NOTE: 予約完了時
     */
    if (finished) {
      return (
        <ReservationDone
          med={med}
          type={reservation.verification_type}
          onClick={() => {
            navigate(0)
            setRead(true)
          }}
        />
      )
    }

    if (read) {
      return (
        <ReservationRead
          med={med}
          settings={departmentSettings}
          reservation={nullToUndefined<ReservationFormProps>(reservation)}
          onSubmitCancel={reservationCancelHandler}
          onclickEditHandler={() => {
            setRead(false)
          }}
          onClickVideoChatStart={videoChatStartHandler}
          billing={billingSettings}
          prescriptionSetting={prescriptionSetting}
        />
      )
    }

    return (
      <>
        <ReservationComponent
          onSubmit={submitHandler}
          med={med}
          defaultValues={{
            saves_data: true, // NOTE: 新規予約時デフォルト値
            applicant_birthday: '1980-01-01', // NOTE: 新規予約時デフォルト値
            ...nullToUndefined<ReservationFormProps>(reservation),
          }}
          onSelectDepartment={selectDepartmentHandler}
          departmentId={departmentId}
          settings={departmentSettings}
          onSetDate={getReservationFrameHandler}
          onChangeCalenderMonth={(date) => {
            // TODO: 実際にはmonthではなくカレンダー内の日付が変わった時なのでメソッド名と実情があってないため上流で月が変わった時だけ発火するように修正する
            setCurrentMonth({
              year: getYear(date),
              month: getMonth(date),
            })
          }}
          reservableDates={reservableDates}
          reservationFrames={reservationFrames}
          onSelectStripe={() => getStripeKeys(access_key)}
          onCancel={() => setRead(true)}
          prescription={{
            nearPharmacies,
            setting: prescriptionSetting,
            onSearch: (params) => searchPharmacies(access_key, params),
            onSearchError: (error) =>
              postGoogleMapsApiError(access_key, errorToString(error)),
            apiKey: googleMapsApiKey || '',
          }}
          eligibilityVerificationUrlSetting={eligibilityVerificationUrlSetting}
        />
      </>
    )
  }

  /**
   * 何もデータがない時はフッタのみ表示
   */
  return <Loading />
}

export default Reservation
